import { useEffect, useState } from 'react';

export const SPECIAL_DAY_LOGO_OPTIONS = [
  { key: '15-temmuz', label: '15 Temmuz', dateKey: '07-15', logoUrl: '/images/15Temmuz.png' },
  { key: '19-mayis', label: '19 Mayıs', dateKey: '05-19', logoUrl: '/images/19Mayis.png' },
  { key: '23-nisan', label: '23 Nisan', dateKey: '04-23', logoUrl: '/images/23Nisan.png' },
  { key: '29-ekim', label: '29 Ekim', dateKey: '10-29', logoUrl: '/images/29Ekim.png' },
  { key: '30-agustos', label: '30 Ağustos', dateKey: '08-30', logoUrl: '/images/30Agustos.png' },
] as const;

export type SpecialDayLogoKey = (typeof SPECIAL_DAY_LOGO_OPTIONS)[number]['key'];

interface ManualLogoOverride {
  logoUrl: string;
  activeDate: string;
}

interface BrandLogoApiResponse {
  brandLogo?: {
    mode?: string;
    logoKey?: unknown;
    activeDate?: unknown;
  };
}

const ISTANBUL_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Istanbul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function getIstanbulDateParts(date: Date) {
  const parts = ISTANBUL_DATE_FORMATTER.formatToParts(date);

  return {
    year: parts.find((part) => part.type === 'year')?.value,
    month: parts.find((part) => part.type === 'month')?.value,
    day: parts.find((part) => part.type === 'day')?.value,
  };
}

export function getIstanbulDateKey(date: Date): string {
  const { year, month, day } = getIstanbulDateParts(date);
  return year && month && day ? `${year}-${month}-${day}` : '';
}

export function getSpecialDayLogo(date: Date): string | null {
  const { month, day } = getIstanbulDateParts(date);
  if (!month || !day) return null;

  return SPECIAL_DAY_LOGO_OPTIONS.find((option) => option.dateKey === `${month}-${day}`)?.logoUrl ?? null;
}

async function fetchManualLogoOverride(): Promise<ManualLogoOverride | null> {
  try {
    const response = await fetch('/api/brand-logo', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!response.ok) return null;

    const data = await response.json().catch(() => null) as BrandLogoApiResponse | null;
    const brandLogo = data?.brandLogo;
    if (brandLogo?.mode !== 'manual') return null;

    const option = SPECIAL_DAY_LOGO_OPTIONS.find((item) => item.key === brandLogo.logoKey);
    const activeDate = typeof brandLogo.activeDate === 'string' ? brandLogo.activeDate : '';
    if (!option || !activeDate) return null;

    return {
      logoUrl: option.logoUrl,
      activeDate,
    };
  } catch {
    return null;
  }
}

export function useSpecialDayLogo(defaultLogo: string): string {
  const [manualOverride, setManualOverride] = useState<ManualLogoOverride | null>(null);
  const [logo, setLogo] = useState(() => getSpecialDayLogo(new Date()) ?? defaultLogo);

  useEffect(() => {
    let cancelled = false;

    void fetchManualLogoOverride().then((override) => {
      if (!cancelled) setManualOverride(override);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const updateLogo = () => {
      const now = new Date();
      const manualLogo = manualOverride?.activeDate === getIstanbulDateKey(now)
        ? manualOverride.logoUrl
        : null;

      setLogo(manualLogo ?? getSpecialDayLogo(now) ?? defaultLogo);
    };
    let intervalId: number | undefined;

    updateLogo();
    const timeoutId = window.setTimeout(() => {
      updateLogo();
      intervalId = window.setInterval(updateLogo, 1000);
    }, 1000 - (Date.now() % 1000));

    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [defaultLogo, manualOverride]);

  return logo;
}
